import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useLenis } from "lenis/react";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import Navbar from "../components/Navbar";
import Seo from "../components/Seo";
import Footer from "../sections/Footer";
import { Reveal, Overline } from "../components/Reveal";
import { useContent } from "../hooks/useContent";

export default function ServiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const lenis = useLenis();
  const { content, isLoading } = useContent();
  const service = content.services.find((s) => s.id === id);
  const others = content.services.filter((s) => s.id !== id);

  useEffect(() => {
    lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [id, lenis]);

  const goBooking = (e) => {
    e.preventDefault();
    navigate("/#booking");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="text-gray-500 text-sm uppercase tracking-[0.3em]">Loading…</p>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="bg-[#050505] text-white min-h-screen font-body" data-testid="service-not-found">
        <div className="grain" />
        <Navbar />
        <main className="pt-40 pb-32 max-w-7xl mx-auto px-5 sm:px-8">
          <Overline>Not Found</Overline>
          <h1 className="mt-4 font-display text-4xl sm:text-6xl font-bold uppercase tracking-tighter">Service not found</h1>
          <p className="mt-6 text-gray-400 max-w-md">That service doesn't exist — explore what we do offer below.</p>
          <div className="mt-10 flex flex-col gap-3">
            {content.services.map((s) => (
              <Link key={s.id} to={`/services/${s.id}`} className="text-electric-bright hover:text-white transition-colors font-display text-xl uppercase tracking-tight">
                {s.title} →
              </Link>
            ))}
            <Link to="/" className="mt-6 text-sm text-gray-400 hover:text-white transition-colors" data-testid="not-found-home-link">← Back to homepage</Link>
          </div>
        </main>
        <Footer content={content} />
      </div>
    );
  }

  return (
    <div className="bg-[#050505] text-white min-h-screen font-body" data-testid="service-detail-page">
      <Seo
        title={`${service.title} in Rochdale | TheMapLab`}
        description={(service.description || "").slice(0, 158)}
        canonical={`https://themaplab.co.uk/services/${service.id}`}
      />
      <div className="grain" />
      <Navbar />
      <main className="pt-16">
        <section className="relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-12">
            <Reveal>
              <Link to="/#services" data-testid="service-back-link" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-gray-500 hover:text-white transition-colors">
                <ArrowLeft size={14} /> All Services
              </Link>
              <div className="mt-8 flex flex-wrap items-end justify-between gap-6">
                <div>
                  <Overline>{service.tagline || "Our Services"}</Overline>
                  <h1 className="mt-4 font-display text-5xl sm:text-7xl font-bold uppercase tracking-tighter leading-[0.95]" data-testid="service-detail-title">
                    {service.title}
                  </h1>
                </div>
                {service.price && (
                  <span className="font-display text-3xl sm:text-4xl font-bold text-electric-bright tracking-tight" data-testid="service-detail-price">
                    {service.price}
                  </span>
                )}
              </div>
            </Reveal>
          </div>
          {service.image && (
            <Reveal delay={0.1}>
              <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <div className="relative h-64 sm:h-96 overflow-hidden border border-white/10">
                  <img src={service.image} alt={service.title} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 via-transparent to-transparent" />
                  <div className="absolute inset-0 bg-[radial-gradient(60%_60%_at_75%_30%,rgba(6,69,243,0.14),transparent_70%)]" />
                </div>
              </div>
            </Reveal>
          )}
        </section>

        <section className="py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.4fr_1fr] gap-14 lg:gap-20">
            <div>
              <Reveal>
                <Overline>Overview</Overline>
              </Reveal>
              {(service.detail_intro?.length ? service.detail_intro : [service.description]).map((p, i) => (
                <Reveal key={i} delay={0.05 + i * 0.06}>
                  <p className="mt-6 text-base sm:text-lg text-gray-300 leading-relaxed">{p}</p>
                </Reveal>
              ))}
              {service.detail_note && (
                <Reveal delay={0.2}>
                  <div className="mt-10 border-l-2 border-electric pl-6 py-1" data-testid="service-detail-note">
                    <p className="text-sm text-gray-400 leading-relaxed">{service.detail_note}</p>
                  </div>
                </Reveal>
              )}
              <Reveal delay={0.25}>
                <div className="mt-12 flex flex-wrap gap-4">
                  <a href="/#booking" onClick={goBooking} data-testid="service-book-button" className="bg-electric hover:bg-electric-bright text-white text-sm font-bold uppercase tracking-[0.18em] px-9 py-4 transition-colors shadow-[0_0_28px_rgba(6,69,243,0.4)]">
                    Book This Service
                  </a>
                  <Link to="/#pricing" data-testid="service-pricing-link" className="border border-white/25 hover:border-white text-white text-sm font-bold uppercase tracking-[0.18em] px-9 py-4 transition-colors">
                    See Pricing
                  </Link>
                </div>
              </Reveal>
            </div>

            <div className="space-y-14">
              {service.includes?.length > 0 && (
                <div>
                  <Reveal>
                    <Overline>What's Included</Overline>
                  </Reveal>
                  <ul className="mt-6 space-y-0">
                    {service.includes.map((item, i) => (
                      <Reveal key={i} delay={i * 0.05}>
                        <li className="flex items-start gap-4 border-t border-white/10 last:border-b py-4" data-testid={`service-include-${i}`}>
                          <span className="mt-0.5 h-5 w-5 shrink-0 border border-electric/50 flex items-center justify-center text-electric-bright">
                            <Check size={12} />
                          </span>
                          <span className="text-sm sm:text-base text-gray-300 leading-relaxed">{item}</span>
                        </li>
                      </Reveal>
                    ))}
                  </ul>
                </div>
              )}
              {service.process?.length > 0 && (
                <div>
                  <Reveal>
                    <Overline>How It Works</Overline>
                  </Reveal>
                  <ol className="mt-6 space-y-0">
                    {service.process.map((step, i) => (
                      <Reveal key={i} delay={i * 0.05}>
                        <li className="flex items-baseline gap-5 border-t border-white/10 last:border-b py-4" data-testid={`service-step-${i}`}>
                          <span className="font-display text-sm font-semibold text-electric-bright shrink-0">{String(i + 1).padStart(2, "0")}</span>
                          <span className="text-sm sm:text-base text-gray-300 leading-relaxed">{step}</span>
                        </li>
                      </Reveal>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </div>
        </section>

        {others.length > 0 && (
          <section className="py-16 sm:py-24 border-t border-white/5">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
              <Reveal>
                <Overline>Explore More</Overline>
              </Reveal>
              <div className="mt-8 grid md:grid-cols-2 gap-5">
                {others.map((s, i) => (
                  <Reveal key={s.id} delay={i * 0.08}>
                    <Link
                      to={`/services/${s.id}`}
                      data-testid={`service-other-${s.id}`}
                      className="group flex items-center justify-between gap-6 border border-white/10 bg-coal p-8 hover:border-electric/60 transition-colors"
                    >
                      <div>
                        <h3 className="font-display text-2xl font-semibold uppercase tracking-tight text-white group-hover:text-electric-bright transition-colors">{s.title}</h3>
                        <p className="mt-1 text-sm text-gray-500">{s.tagline}</p>
                        {s.price && <p className="mt-3 text-sm font-bold text-electric-bright">{s.price}</p>}
                      </div>
                      <ArrowRight size={20} className="text-gray-600 group-hover:text-electric-bright group-hover:translate-x-1 transition-all shrink-0" />
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer content={content} />
    </div>
  );
}
